'use client';

import { ArrowLeft, ArrowRight } from '@1debit/icons';
import { cn } from 'utils/styles';

const PAGINATION_WINDOW = 2;

interface PaginationControlsProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

function getPageNumbers(currentPage: number, totalPages: number): (number | 'ellipsis')[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | 'ellipsis')[] = [1];

  const rangeStart = Math.max(2, currentPage - PAGINATION_WINDOW);
  const rangeEnd = Math.min(totalPages - 1, currentPage + PAGINATION_WINDOW);

  if (rangeStart > 2) {
    pages.push('ellipsis');
  }

  for (let i = rangeStart; i <= rangeEnd; i++) {
    pages.push(i);
  }

  if (rangeEnd < totalPages - 1) {
    pages.push('ellipsis');
  }

  pages.push(totalPages);

  return pages;
}

const pageButtonBase = 'flex items-center justify-center size-6 font-bold text-content-primary';
const arrowButtonBase = 'flex items-center gap-1 font-bold text-content-primary transition-colors';

const PaginationControls = ({ currentPage, totalPages, onPageChange }: PaginationControlsProps) => {
  if (totalPages <= 1) return null;

  const pages = getPageNumbers(currentPage, totalPages);
  const hasPrev = currentPage > 1;
  const hasNext = currentPage < totalPages;

  return (
    <nav aria-label="Pagination" className="flex items-center justify-center gap-3 mt-12">
      <button
        type="button"
        onClick={() => hasPrev && onPageChange(currentPage - 1)}
        disabled={!hasPrev}
        className={cn(arrowButtonBase, hasPrev ? 'cursor-pointer' : 'opacity-50 cursor-default', 'mr-14')}
        aria-label="Previous page">
        <ArrowLeft className="size-5" />
        <span>Previous</span>
      </button>

      {pages.map((page, index) => {
        if (page === 'ellipsis') {
          return (
            <span key={`ellipsis-${index}`} className={pageButtonBase} aria-hidden="true">
              &hellip;
            </span>
          );
        }

        const isActive = page === currentPage;
        return (
          <button
            type="button"
            key={page}
            onClick={() => onPageChange(page)}
            className={cn(pageButtonBase, 'cursor-pointer', isActive && 'opacity-50')}
            aria-label={`Page ${page}`}
            aria-current={isActive ? 'page' : undefined}>
            {page}
          </button>
        );
      })}

      <button
        type="button"
        onClick={() => hasNext && onPageChange(currentPage + 1)}
        disabled={!hasNext}
        className={cn(arrowButtonBase, hasNext ? 'cursor-pointer' : 'opacity-50 cursor-default', 'ml-14')}
        aria-label="Next page">
        <span>Next</span>
        <ArrowRight className="size-5" />
      </button>
    </nav>
  );
};

export default PaginationControls;
